import { primeServices } from "@services/apis/Prime";
import { AdminReverseEscrowRequest } from "@services/models/Prime/AdminReverseRequest";
import { AdminSettleEscrowRequest } from "@services/models/Prime/AdminSettleRequest";
import { GetDisputedTradeListData } from "@services/models/Prime/GetDisputedTradeListModel";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import React, { useState } from "react";
import CopyButton from "@components/buttons/CopyButton";
import CustomButton from "@components/buttons/CustomButton";
import ChatMessagesPrimeBuySell from "./ChatMessagesPrimeBuySell";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { pdf } from "@react-pdf/renderer";
import KYCPdfPrimeBuySellViewer from "@components/kycpdf/KycPrimePdfBuySellView";
import ReverseAlertSlider from "./ReverseAlertSlider";
import SettleAlertSlider from "./SettleAlertSlider";

interface PrimeDisputeChatProps {
  tradeData: GetDisputedTradeListData;
  onDisputeResoultion?: (tradeId: string) => void;
  userId: number;
}

const PrimeDisputeChat: React.FC<PrimeDisputeChatProps> = ({
  tradeData,
  onDisputeResoultion,
  userId,
}) => {
  const [reversePopUp, setReversePopUp] = useState<boolean>(false);
  const [settlePopUp, setSettlePopUp] = useState<boolean>(false);

  const [adminSettleEscrowLoading, setAdminSettleEscrowLoading] =
    useState<boolean>(false);
  const [adminReverseEscrowLoading, setAdminReverseEscrowLoading] =
    useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [fetchKYCLoading, setFetchKycLoading] = useState(false);

  const getKYCData = async (ismaker: boolean) => {
    try {
      setFetchKycLoading(true);
      const response = await primeServices.getCustomerServiceP2Pkyc({
        tradeId: tradeData!.tradeId.toString(),
      });

      const kycData = response.data;

      const isMaker = ismaker;

      // Generate the PDF document as a Blob
      if ((isMaker && kycData.takerKyc) || (!isMaker && kycData.makerKyc)) {
        const pdfBlob = await pdf(
          <KYCPdfPrimeBuySellViewer
            tradeData={tradeData!}
            viewerKycDetails={!isMaker ? kycData.takerKyc : kycData.makerKyc}
          />
        ).toBlob();

        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      } else {
        handleError({
          message: "KYC data not found",
        });
      }
    } catch (error: any) {
      handleError({
        message: "KYC data not found",
      });
    } finally {
      setFetchKycLoading(false);
    }
  };

  const handleSettleEscrow = async () => {
    const event = window.event as MouseEvent;
    event?.stopPropagation();

    const tradeActionRequest: AdminSettleEscrowRequest = {
      tradeId: tradeData.tradeId.toString(),
    };

    try {
      setAdminSettleEscrowLoading(true);
      const response = await primeServices.adminSettleEscrow(
        tradeActionRequest
      );
      handleSuccess({ message: response.msg });
      onDisputeResoultion?.(tradeData.tradeId.toString());
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ??
          "An error occurred while settling escrow. Please try again.",
      });
    } finally {
      setAdminSettleEscrowLoading(false);
    }
  };

  const handleReverseEscrow = async () => {
    const event = window.event as MouseEvent;
    event?.stopPropagation();

    const tradeActionRequest: AdminReverseEscrowRequest = {
      tradeId: tradeData.tradeId.toString(),
    };

    try {
      setAdminReverseEscrowLoading(true);
      const response = await primeServices.adminReverseEscrow(
        tradeActionRequest
      );
      handleSuccess({ message: response.msg });
      onDisputeResoultion?.(tradeData.tradeId.toString());
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ??
          "An error occurred while reversing escrow. Please try again.",
      });
    } finally {
      setAdminReverseEscrowLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 20) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  return (
    <div className="flex flex-col gap-[12px] mt-[56px] mr-[16px] w-1/3">
      <div className="flex flex-col space-y-[20px] border border-border-table bg-bg-page-body rounded-lg">
        <div className="flex flex-row bg-bg-base py-[6px] gap-[8px] px-[20px]">
          <p className="text-text-base text-[12px] font-semibold">
            {tradeData.buyer}
          </p>
          <p className="text-text-base text-[12px] font-semibold">
            {tradeData.seller}
          </p>
        </div>
        <div className="flex flex-col gap-[12px] px-[12px]">
          <div className="flex flex-row items-center justify-between w-full">
            <div className="flex flex-col space-y-[4px]">
              <p className="text-[12px] text-text-label font-medium">
                Fiat amount
              </p>
              <span className="text-[12px] text-text-base font-semibold">
                {Number(tradeData?.fiatAmount).toFixed(2) ?? 0.0}{" "}
                {/* {tradeData?.fiatCurrency.symbol} */}
              </span>
            </div>
            <div className="flex flex-col space-y-[4px]">
              <p className="text-[12px] text-text-label font-medium">
                Crypto amount
              </p>
              <span className="text-[12px] text-text-base font-semibold text-right">
                {Number(tradeData?.cryptoAmount).toFixed(2) ?? 0.0}{" "}
                {/* {tradeData?.cryptoCurrency.symbol} */}
              </span>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row gap-[4px] items-center">
              <p className="text-text-label text-[12px] font-normal">
                Trade ID
              </p>
              <p className="text-text-base text-[12px] font-normal">
                {tradeData.tradeId}
              </p>
              <CopyButton
                copyText={tradeData.tradeId.toString() ?? ""}
                copyMessage="Trade ID Copied to clipboard"
              />
            </div>
            <div className="flex flex-row gap-[4px] items-center">
              <p className="text-text-label text-[12px] font-normal">UTR</p>
              <p className="text-text-base text-[12px] font-normal">
                {tradeData.utr ?? "- -"}
              </p>
              {tradeData.utr && (
                <CopyButton
                  copyText={tradeData.utr ?? ""}
                  copyMessage="UTR Copied to clipboard"
                />
              )}
            </div>
          </div>
          <div className="border-b border-border-subtle"></div>
          <div className="flex flex-row justify-between items-center">
            <p className="text-text-base text-[12px] font-medium">Cx Details</p>
            <div className="flex items-center gap-[8px]">
              <div className="flex items-center">
                <p className="text-text-base text-[12px]">
                  {tradeData.escalatorMobileNumber
                    ? tradeData.escalatorMobileNumber
                    : "- -"}
                </p>
                {tradeData.escalatorMobileNumber && (
                  <CopyButton
                    copyText={tradeData.escalatorMobileNumber ?? ""}
                    copyMessage="Mobile number copied"
                  />
                )}
              </div>
              <div className="flex items-center">
                <p className="text-text-base text-[12px]">
                  {tradeData.escalatorEmailId
                    ? tradeData.escalatorEmailId
                    : "- -"}
                </p>
                {tradeData.escalatorEmailId && (
                  <CopyButton
                    copyText={tradeData.escalatorEmailId ?? ""}
                    copyMessage="Email ID copied"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center">
            <p className="text-text-base text-[12px] font-medium">
              Dispute Reason
            </p>
            <div className="flex items-center">
              <p className="text-text-base text-[12px]">
                {tradeData.disputeMessage
                  ? truncateText(tradeData.disputeMessage)
                  : "- -"}
              </p>
              {tradeData.disputeMessage && (
                <CopyButton
                  copyText={tradeData.disputeMessage ?? ""}
                  copyMessage="Dispute reason copied"
                />
              )}
            </div>
          </div>

          <div className="border-b border-border-subtle"></div>
          <div className="flex flex-row items-center justify-between">
            <CustomButton
              text={"REVERSE"}
              isActive={true}
              onClick={() => setReversePopUp(true)}
              isButtonLoading={adminReverseEscrowLoading}
            />
            <CustomButton
              text={"SETTLE"}
              isActive={true}
              onClick={() => setSettlePopUp(true)}
              isButtonLoading={adminSettleEscrowLoading}
            />
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-base font-medium text-[14px]">KYC</p>
            <div className="flex flex-row gap-[4px] items-center">
              <LightCustomButton
                text={"Cx KYC"}
                isActive={true}
                className="text-[8px]"
                onClick={() => getKYCData(true)}
                isButtonLoading={fetchKYCLoading}
              />
              <LightCustomButton
                text={"CASHIER KYC"}
                isActive={true}
                className="text-[8px]"
                onClick={() => getKYCData(false)}
                isButtonLoading={fetchKYCLoading}
              />
            </div>
          </div>
          <div className="border-b border-border-subtle mb-4"></div>
          <ChatMessagesPrimeBuySell
            tradeId={tradeData.tradeId}
            userId={userId!}
          />
        </div>
      </div>
      <ReverseAlertSlider
        isOpen={reversePopUp}
        onClose={() => setReversePopUp(false)}
        onClick={handleReverseEscrow}
        escrowAmount={tradeData.cryptoAmount.toString()}
        buttonLoading={adminReverseEscrowLoading}
      />
      <SettleAlertSlider
        isOpen={settlePopUp}
        onClose={() => setSettlePopUp(false)}
        onClick={handleSettleEscrow}
        escrowAmount={tradeData.cryptoAmount.toString()}
        buttonLoading={adminSettleEscrowLoading}
      />
    </div>
  );
};

export default PrimeDisputeChat;
